import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Section } from "./ui/section";
import { CurrencyCombobox } from "./CurrencyCombobox";
import { LanguageCombobox, type LanguageCode } from "./LanguageCombobox";
import { useDeleteGroup, useUpdateGroup } from "../api/hooks";
import type { Group } from "../api/types";

/**
 * Group settings form — rename, change base currency, or delete the group.
 * Rendered on the dedicated `/groups/:groupId/settings` screen (owner-only).
 */
export function GroupSettingsPanel({ group }: { group: Group }): ReactNode {
  const navigate = useNavigate();
  const updateGroup = useUpdateGroup(group.id);
  const deleteGroup = useDeleteGroup();

  const [label, setLabel] = useState(group.label);
  const [currency, setCurrency] = useState(group.currency);
  const [language, setLanguage] = useState<LanguageCode>(group.language);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    const patch: {
      label?: string;
      currency?: typeof currency;
      language?: typeof language;
    } = {};
    if (label !== group.label) patch.label = label;
    if (currency !== group.currency) patch.currency = currency;
    if (language !== group.language) patch.language = language;
    if (Object.keys(patch).length === 0) return;
    await updateGroup.mutateAsync(patch);
  }

  async function onDelete(): Promise<void> {
    await deleteGroup.mutateAsync(group.id);
    navigate("/");
  }

  const dirty =
    label !== group.label ||
    currency !== group.currency ||
    language !== group.language;

  return (
    <div className="space-y-stack-lg">
      <Section
        title="Settings"
        description="Rename your group, change its base currency, or set the language scanned receipts are translated into."
      >
        <form className="space-y-5" onSubmit={onSave}>
          <div className="space-y-2">
            <Label htmlFor="group-label">Name</Label>
            <Input
              id="group-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              minLength={1}
              maxLength={120}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-currency">Currency</Label>
            <CurrencyCombobox
              id="group-currency"
              value={currency}
              onChange={setCurrency}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-language">Receipt language</Label>
            <LanguageCombobox
              id="group-language"
              value={language}
              onChange={setLanguage}
            />
            <p className="text-label-sm text-on-surface-variant">
              Scanned receipts in another language are translated into this one.
            </p>
          </div>
          {updateGroup.error ? (
            <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
              Couldn't save changes. Please try again.
            </div>
          ) : null}
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={!dirty || updateGroup.isPending || !label}
            >
              {updateGroup.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </Section>

      <Section
        title="Danger zone"
        description="Deleting a group is permanent and affects everyone in it."
      >
        {confirmDelete ? (
          <div className="space-y-3">
            <p className="text-body-md">
              Delete <span className="font-medium">{group.label}</span> for
              everyone? This removes all expenses and comments and cannot be
              undone.
            </p>
            {deleteGroup.error ? (
              <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
                Couldn't delete the group.
              </div>
            ) : null}
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteGroup.isPending}
              >
                Keep group
              </Button>
              <Button
                variant="destructive"
                onClick={onDelete}
                disabled={deleteGroup.isPending}
              >
                {deleteGroup.isPending ? "Deleting…" : "Delete forever"}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="size-4" />
            Delete group
          </Button>
        )}
      </Section>
    </div>
  );
}
